/** Shared admin API helpers for Team / user login management. */
import { supabase } from './supabase'

export async function adminFetch(
  path: string,
  session: { access_token: string } | null,
  options: RequestInit = {}
) {
  const doFetch = async (accessToken: string | null | undefined) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`
    const res = await fetch(path, { ...options, headers })
    const data = await res.json().catch(() => ({}))
    return { res, data }
  }

  let token = session?.access_token || null
  if (!token) {
    const { data } = await supabase.auth.getSession()
    token = data.session?.access_token || null
  }

  let { res, data } = await doFetch(token)

  // Stale JWT often surfaces as 401/403 Forbidden from the admin API.
  if (!res.ok && (res.status === 401 || res.status === 403)) {
    const { data: refreshed } = await supabase.auth.refreshSession()
    const nextToken = refreshed.session?.access_token || null
    if (nextToken && nextToken !== token) {
      token = nextToken
      ;({ res, data } = await doFetch(token))
    }
  }

  if (!token) throw new Error('Not signed in — refresh the page and try again')
  if (!res.ok) throw new Error(data.error || res.statusText || 'Request failed')
  return data
}

export function formatAdminApiError(message: string) {
  if (
    message === 'Not Found' ||
    message === 'Gateway Timeout' ||
    message.includes('trying to proxy') ||
    message.includes('ECONNREFUSED') ||
    /timeout/i.test(message)
  ) {
    return 'API server not running. Start it in a second terminal: npm run proxy-server'
  }
  return message
}

export interface ManagedUser {
  id: string
  email: string
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  role: string
  is_active: boolean
  primary_division_id?: string | null
  customer_id?: string | null
  invite_pending?: boolean
  last_sign_in_at?: string | null
  effective_permissions?: string[]
  is_super_admin?: boolean
  can_create_super_admins?: boolean
  tenant_id?: string | null
  tenant?: { id: string; name: string; slug?: string; status?: string } | null
}

export type LoginAccessFormState = {
  email: string
  first_name: string
  last_name: string
  phone: string
  role: string
  is_active: boolean
  customer_id: string
  permissions: string[]
  customized: boolean
  /** New login password, or optional new password when editing an existing login */
  password: string
  /** invite = email invite; create = password now; link = already exists (handled outside) */
  createMode: 'invite' | 'create'
}
